
import { h, Component } from 'preact';
import { accessNested, joinClasses, sortObjectByOrder } from 'utils/utils';
import { getValueForClientSize, isVideosPage } from 'utils/bolt';
import { useDynamicAssetFolder } from 'utils/assets';
import * as webp from 'utils/bolt/webp';
import { smoothScrollBy, smoothScrollTo } from 'utils/smoothScroll';
import VideoPlayer from 'components/bolt/videos/player';
import HTML from 'components/core/html/html';
import Store from 'store/store';
import parseColor from 'color-parse';

import s from 'components/bolt/tiledItems/tile/tile.sss';

export default class Tile extends Component {
	constructor(props) {
		super(props);

		this.state = {
			isOverflown: false
		};

		this.isVerticallyOverflown = this.isVerticallyOverflown.bind(this);
		this.renderButton = this.renderButton.bind(this);
		this.onLoadedHandler = this.onLoadedHandler.bind(this);
		this.onEndedHandler = this.onEndedHandler.bind(this);
		this.onScroll = this.onScroll.bind(this);
		this.scrollBody = this.scrollBody.bind(this);
		this.onImageClick = this.onImageClick.bind(this);
	}

	componentDidMount() {
		this.resizeObserver = new ResizeObserver(this.isVerticallyOverflown);
		this.resizeObserver.observe(this.base);
		this.setOverflowColor();
		this.isVerticallyOverflown();
	}

	componentWillUnmount() {
		this.resizeObserver?.disconnect();
	}

	onImageClick(item) {
		if (!item?.path) {
			return;
		}
		window.open(item?.path);
	}

	getNaturalContentHeight() {
		if (!this.$content) {
			return 0;
		}
		const styleHeight = this.$content.style.height;
		this.$content.style.height = '';
		const height = this.$content.clientHeight;
		this.$content.style.height = styleHeight;
		return height;
	}

	setOverflowColor() {
		const shadow = this.$tile.parentNode?.parentNode;
		const background = shadow?.parentNode?.parentNode;
		const { values: shadowColorValues, alpha } = parseColor(getComputedStyle(shadow).backgroundColor) || {};
		const { values: backgroundColorValues } = parseColor(getComputedStyle(background).backgroundColor) || {};

		const r = (shadowColorValues[0] * alpha) + (backgroundColorValues[0] * (1 - alpha));
		const g = (shadowColorValues[1] * alpha) + (backgroundColorValues[1] * (1 - alpha));
		const b = (shadowColorValues[2] * alpha) + (backgroundColorValues[2] * (1 - alpha));

		this.setState({ overflowColor: `rgb(${r}, ${g}, ${b})` });
	}

	onScroll() {
		const isOverflown = this.state.isOverflown;
		const elem = this.$body;
		this.setState({
			scrollableTop: isOverflown && elem.scrollTop > 0,
			scrollableBottom: isOverflown && elem.scrollTop < elem.scrollHeight - elem.clientHeight
		});
	}

	isVerticallyOverflown() {
		const isOverflown = this.$body && this.$body.scrollHeight > this.$body.clientHeight;
		this.setState({ isOverflown }, this.onScroll);
	}

	scrollBody() {
		if (this.state.scrollableBottom) {
			smoothScrollBy(this.$body, 50, 300);
		} else {
			smoothScrollTo(this.$body, 0, 300);
		}
	}

	getSrc(activeImage) {
		let src;
		if (!activeImage.mobileSrc && !activeImage.tabletSrc) {
			src = activeImage.desktopSrc;
		} else {
			src = {
				desktop: activeImage.desktopSrc,
				mobile: activeImage.mobileSrc,
				tablet: activeImage.tabletSrc
			};
		}
		return useDynamicAssetFolder(getValueForClientSize(src));
	}

	async triggerAction(path) {
		let newPath = path;
		let queryString = '';

		if (path.includes('?v=')) {
			const stringArray = path.split('?v=');
			newPath = stringArray[0];
			queryString = '?v=' + stringArray[1];
		}

		if (path.includes('?m=')) {
			const stringArray = path.split('?m=');
			newPath = stringArray[0];
			queryString = '?m=' + stringArray[1];
		}

		// We should use navigateInternally from utils/bolt here
		if (path.startsWith('@')) {
			return Store.emit('OPEN_RESERVED_VARIABLE_LINK', path);
		} else if (isVideosPage(newPath)) {
			return Store.emit('OPEN_VIDEO_OVERLAY', newPath, queryString);
		} else if (path.includes('boltShowtimes')) {
			return Store.emit('SHOWTIMES_SHOW', true, queryString);
		} else if (path === 'boltHomeEnt') {
			return Store.emit('HOMEENT_SHOW', true);
		} else if (path === 'scrollTop') {
			smoothScrollTo(document.getElementById('mainScroll'), 0, 300);
		} else {
			const isSection = accessNested(Store.get(), `appData.pages.${path}.section`, false);

			if (isSection) {
				return Store.emit('CHANGE_ACTIVE_SECTION', path);
			}

			// await animation(mainScroll, 'left', 400);
			return Store.emit('SET_ACTIVE_PAGE', path);
		}
	}

	onEndedHandler = (activeVideo) => {
		Store.emit('VIDEO_ENDED', activeVideo);
	}

	onLoadedHandler() {
		this.setState({ loading: false });
	}

	getFallbackFormat() {
		return accessNested(this.props, `appData.pages.${this.props.id}.data.mediaContent.alternativeImageFormat`, 'jpg');
	}

	renderVideo(video) {
		const loading = this.state.loading;
		const pageId = this.props?.id;
		const pageData = this.props?.appData?.pages[pageId]?.data || {};

		return (
			<div
				class={joinClasses(s.videoContainer, loading && s.loading)}
				ref={ref => this.$videoContainer = ref}
			>
				<VideoPlayer
					ref={ref => this.$videoPlayer = ref}
					activeVideo={video}
					onLoaded={this.onLoadedHandler}
					onEnded={() => this.onEndedHandler(video)}
					overrideStyles
					noAutoplay
					muted={pageData.autoMuteVideos}
				/>
			</div>
		);
	}

	renderImage(item) {
		const { loading } = this.state;
		const { isPortrait } = this.props;
		const image = this.getSrc(item);
		const fallbackFormat = this.getFallbackFormat();

		return (
			<div class={joinClasses(s.imageWrapper, !isPortrait ? s.keepAspectRatio : s.portrait)}>
				<webp.Picture
					class={joinClasses(loading && s.loading, item?.path && s.clickable)}
					src={image}
					fallbackFormat={fallbackFormat}
					alt={item?.alt}
					ariaLabel={item?.alt}
					onLoad={this.imageLoaded}
					ref={$ref => this.$galleryImage = $ref}
					key="image"
					onClick={() => this.onImageClick(item)}
					onMouseDown={this.onDragStart}
					onTouchStart={this.onDragStart}
					onMouseMove={this.onDrag}
					onTouchMove={this.onDrag}
					onMouseUp={this.onDragEnd}
					onTouchEnd={this.onDragEnd}
				/>
			</div>
		);
	}

	renderButton(button) {
		const link = button.link?.trim();

		const content = [
			<span>{button.label}</span>,
			button.icon && <img class={s.icon} src={button.icon} alt="" />
		];

		if (/^(https?|mailto):/.test(link)) {
			return (
				<a class={s.cta} href={link} target="_blank" title={button.title}>
					{content}
				</a>
			);
		}

		return (
			<button class={s.cta} onClick={() => this.triggerAction(link)} title={button.title}>
				{content}
			</button>
		);
	}

	renderButtons(content) {
		const { isOverflown } = this.state;
		const buttonsArray = sortObjectByOrder(content);
		const buttons = buttonsArray.map(this.renderButton);

		return (
			<div ref={ref => this.$ctas = ref} class={joinClasses(s.ctas, (!isOverflown || !buttons) && s.noFade)}>
				<div class={s.bottomLine} />
				<div class={s.buttons}>
					{buttons}
				</div>
			</div>
		);
	}

	render(props, state) {
		const { data, isPortrait, height = '' } = props;
		const { title, description, media, body, ctaButtons } = data;
		const mediaItem = media && Object.values(media)?.[0];
		const isVideo = mediaItem && ['videoSrc', 'vimeoId', 'youtubeId', 'disneySrc', 'youtubePlaylistId'].some(id => mediaItem[id]);

		const { scrollableTop, scrollableBottom, overflowColor } = state;

		const bodyContainerStyle = overflowColor ? `--shadow-color: ${overflowColor}` : '';

		return 	(
			<div ref={ref => this.$tile = ref} class={joinClasses(s.tile, isPortrait && s.portrait)}>
				{!!mediaItem && (isVideo ? this.renderVideo(mediaItem) : this.renderImage(mediaItem))}
				<div ref={ref => this.$content = ref} class={s.content} style={{ height }}>
					<div class={s.innerContent}>
						{title && <HTML class={joinClasses(s.title, 'h3')} content={title} markdown ref={e => this.$tileTitle = e?.base} />}
						{description && <HTML class={s.description} content={description} markdown ref={e => this.$tileDescription = e?.base} />}
						{body && (
							<div class={s.container}>
								<div class={joinClasses({ s, bodyContainer: true, noButtons: !ctaButtons, scrollableTop, scrollableBottom })} style={bodyContainerStyle}>
									<HTML tag="p" class={s.body} content={body} markdown ref={e => this.$body = e?.base} onScroll={this.onScroll} />
									<button class={joinClasses(s.arrow, !scrollableBottom && s.topArrow)} onClick={this.scrollBody} />
								</div>
								{this.renderButtons(ctaButtons)}
							</div>
						)}
					</div>
					<div class={s.fillerRight} />
				</div>
			</div>
		);
	}
}
